import { groupBy, includes, isFunction, keyBy, map, reduce, orderBy, } from 'lodash';
import { useMemo } from 'react';
import Modules from '../../../modules';
import useUserPreferences from '../hooks/useUserPreferences';
import makeFeaturedPermissionHelpers from '../../components/utils/FeaturedPermission/makeFeaturedPermissionHelpers';
const FIRST_ITEM = 0;
const DEFAULT_ROUTE_DEPTH = 2;
export function useModules() {
    const { me, accessList, preferences } = useUserPreferences();
    const defaultRoutes = useMemo(() => keyBy(preferences.LAST_PATHNAME_MODULES, 'module'), [preferences.LAST_PATHNAME_MODULES]);
    const modules = useMemo(() => getModules({
        accessList,
        me,
        defaultRoutes,
    }), [defaultRoutes, accessList, me]);
    const restrictedModules = useMemo(() => getRestrictedModules({
        accessList,
        me,
    }), [accessList, me]);
    const groups = useMemo(() => collectGroups(modules, Modules.groups, defaultRoutes, accessList, me), [defaultRoutes, modules, accessList, me]);
    const setupGroups = useMemo(() => collectGroups(modules, Modules.setupGroups, defaultRoutes, accessList, me), [defaultRoutes, modules, accessList, me]);
    const setupSubGroups = useMemo(() => collectGroups(modules, Modules.setupSubGroups, defaultRoutes, accessList, me), [defaultRoutes, modules, accessList, me]);
    const profileGroups = useMemo(() => collectGroups(Modules.profileGroups[FIRST_ITEM].modules, Modules.profileGroups, defaultRoutes, accessList, me), [me, defaultRoutes, accessList]);
    const byId = useMemo(() => modules.reduce((hash, module) => {
        hash[module.id] = module;
        return hash;
    }, {}), [modules]);
    return {
        groups,
        setupGroups,
        setupSubGroups,
        profileGroups,
        all: modules,
        byId,
        restrictedModules,
    };
}
export const getModules = ({ me, accessList, defaultRoutes, }) => {
    const filtered = Modules.all.filter(({ id, isPermissionCheckSkipped, permission }) => id === 'error' ||
        isPermissionCheckSkipped ||
        !permission ||
        (isFunction(permission)
            ? permission(me)
            : includes(accessList, permission)));
    return defaultRouteMap(filtered, defaultRoutes, accessList, me);
};
export const getRestrictedModules = ({ me, accessList, }) => Modules.all.filter(({ id, isPermissionCheckSkipped, permission }) => id !== 'error' &&
    !isPermissionCheckSkipped &&
    permission &&
    !(isFunction(permission)
        ? permission(me)
        : includes(accessList, permission)));
const defaultRouteMap = (modules, defaultRoutes = {}, accessList, me) => map(modules, module => {
    var _a;
    const routesDescription = isFunction(module.structure)
        ? module.structure(me || {})
        : module.structure;
    const helper = makeFeaturedPermissionHelpers(accessList, module);
    const savedRoute = (_a = defaultRoutes[module.id]) === null || _a === void 0 ? void 0 : _a.route;
    const defaultSavedTab = savedRoute
        ? savedRoute.split('/').slice(0, DEFAULT_ROUTE_DEPTH).join('/')
        : '';
    const accessibleTabs = reduce(routesDescription, (acc, { permission, route }) => {
        if (helper.enabledPermissionByAccessList(permission)) {
            acc.add(`${module.route}/${route}`);
        }
        return acc;
    }, new Set());
    const defaultRoute = (accessibleTabs.has(defaultSavedTab) && savedRoute) ||
        (accessibleTabs.has(module.defaultRoute) && module.defaultRoute) ||
        (module.hasDynamicDefaultRoute && accessibleTabs.values().next().value) ||
        module.route;
    return {
        ...module,
        routesDescription: routesDescription || [],
        defaultRoute,
    };
});
const collectGroups = (modules, modulesGroups, defaultRoutes, accessList, me) => modulesGroups.map(group => ({
    ...group,
    modules: defaultRouteMap(groupModules(group.id, modules, group.hasSubGroups), defaultRoutes, accessList, me),
}));
const groupModules = (groupName, modules, hasSubGroups) => {
    const filteredModules = modules.filter(m => m.group === groupName);
    if (hasSubGroups) {
        const grouped = groupBy(filteredModules, 'subGroup');
        return Object.keys(grouped).map(key => ({
            ...Modules.moduleGroupsMeta[key],
            modules: grouped[key],
        }));
    }
    return orderBy(filteredModules, 'order');
};
